"use client";

import { useEffect, useState } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Box,
  CircularProgress,
  Avatar,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useForm } from "react-hook-form";
import { loginUser } from "@api/auth";
import { gtmPushLogin } from "@utils/gtm";
import { setAccessToken } from "@utils/getAccessToken";
import { useAppContext } from "@context/state";
import {
  getReferralCodeFromSession,
  removeReferralCode,
} from "@utils/getReferralCode";
import { calculateProfilePercent } from "@utils/profileProgress";
import { updateProfileProgress, getLastPageRoute } from "@api/profileProgress";
import { get } from "lodash";
import { useRouter } from "next/router";
import { EMAIL } from "@utils/constants";
import GoogleLoginButton from "@components/Auth/GoogleLoginButton";

const testimonials = [
  {
    id: 1,
    name: "C. Madrigal",
    text: "It’s nice to forecast the financial aspects of the college process… I was doing the same process, manually using excel but College Aid Pro is much easier as you simply type the college name after filling in simple and basic profile info!",
    rating: 5,
    avatar: "C",
    banner: "/images/carousel-login-1.png",
    href: "https://maps.app.goo.gl/njjzUJ8qsPhV9SQ48",
  },
  {
    id: 2,
    name: "J. Griffin",
    text: "I've done a number of webinars on the college application/financial process. The guys at College Aid Pro are by far the most knowledgeable and have the clearest presentation skills. It's well worth investing in their MyCAP software. Easy to use, tons of information.",
    rating: 5,
    avatar: "J",
    banner: "/images/carousel-login-2.png",
    href: "https://www.google.com/maps/contrib/111395174475424812934/reviews?hl=en",
  },
  {
    id: 3,
    name: "S. Ninburg",
    text: "We found College Aid Pro services to be incredibly helpful, accurate, and very much tailored to address the most relevant financial issues for choosing a college. ",
    rating: 5,
    avatar: "S",
    banner: "/images/carousel-login-3.png",
    href: "https://www.google.com/maps/contrib/112063546688468635400/reviews?hl=en",
  },
  {
    id: 4,
    name: "A. Finkle",
    text: "Great tool for estimating costs for each college. The financial aid amount was pretty close to what we actually received. Highly recommend it to all.",
    rating: 5,
    avatar: "A",
    banner: "/images/carousel-login-4.png",
    href: "https://www.google.com/maps/contrib/114682265477101981911/reviews?hl=en",
  },
  {
    id: 5,
    name: "J. Freeman",
    text: "We had a great call with CAP. Worth every penny. They answered our questions in detail and listened carefully to what we were asking. All of our interactions with College Aid Pro have been productive - webinars, portal, correspondence, and one-on-one sessions.",
    rating: 5,
    avatar: "J",
    banner: "/images/carousel-login-5.png",
    href: "https://www.google.com/maps/contrib/118031442020100874624/reviews?hl=en",
  },
  {
    id: 6,
    name: "M. Chamieh",
    text: "Super helpful; wish we had known about this junior year. We are spreading the word to other parents.",
    rating: 5,
    avatar: "M",
    banner: "/images/carousel-login-1.png",
    href: "https://www.google.com/maps/contrib/113330298795937962172/reviews?hl=en",
  },
  {
    id: 7,
    name: "G. Johnson",
    text: "Too good to be true? No. The CAP team really are motivated to help families find the right resources for attending college, and they offer various levels of automated and interactive services to fit a wide range of budgets.",
    rating: 5,
    avatar: "G",
    banner: "/images/carousel-login-2.png",
    href: "https://www.google.com/maps/contrib/104405760921715589452/reviews?hl=en",
  },
  {
    id: 8,
    name: "L. Cirella",
    text: "Between the free seminars and exceptional personal one-on-one sessions customized to my needs, this program was exceptional in helping me wade through forms, loan options and college offers.",
    rating: 5,
    avatar: "L",
    banner: "/images/carousel-login-3.png",
    href: "https://www.google.com/maps/contrib/104214132405504611570/reviews?hl=en",
  },
  {
    id: 9,
    name: "thomas19",
    text: "Great resource for families to gain understanding of the financial landscape associated with higher education, in particular, the financial realities.",
    rating: 5,
    avatar: "T",
    banner: "/images/carousel-login-4.png",
    href: "https://www.google.com/maps/contrib/112481167825440804916/reviews?hl=en",
  },
  {
    id: 10,
    name: "J. DiTrapani",
    text: "Such a great help navigating the process, and simplified all of the terms that I had no idea about, it was so useful for parents of high schoolers.",
    rating: 5,
    avatar: "J",
    banner: "/images/carousel-login-5.png",
    href: "https://www.google.com/maps/contrib/109496501164279295546/reviews?hl=en",
  },
  {
    id: 11,
    name: "T. Schlotter",
    text: "I did the free seminars and then a paid subscription. The one-on-one consultation was very informative, highly recommend.",
    rating: 5,
    avatar: "T",
    banner: "/images/carousel-login-1.png",
    href: "https://www.google.com/maps/contrib/110136310829072120480/reviews?hl=en",
  },
  {
    id: 12,
    name: "B. Averett",
    text: "When it comes to complicated financial questions for applying for financial aid, these are the experts to talk to!",
    rating: 5,
    avatar: "B",
    banner: "/images/carousel-login-2.png",
    href: "https://www.google.com/maps/contrib/108783702366925307070/reviews?hl=en",
  },
];

function LoginNewVer() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    watch,
  } = useForm();
  const {
    setLoggedIn,
    scenario,
    setScenario,
    handleApiError,
    checkForNeededDashboardInfo,
    ssoUser,
  } = useAppContext();

  const router = useRouter();
  const { sso } = router.query;

  useEffect(() => {
    const fetchData = async () => {
      if (sso) {
        await ssoUser(sso);
      }
    };
    fetchData();
  }, [sso]);

  const onSubmit = async (data) => {
    try {
      const res = await loginUser(data);
      if (res.data?.jwt) {
        // push event to Google Tag Manager
        gtmPushLogin(EMAIL);
        // set tokens
        localStorage.setItem("refresh_token", res.data.jwt?.refresh_token);
        setAccessToken(res.data.jwt?.access_token);
        setLoggedIn(true);
        // remove referral code if present
        if (getReferralCodeFromSession()) {
          removeReferralCode();
        }
        // set scenario for instant visual on name and efcs
        setScenario({
          ...scenario,
          case_id: res.data.case_id,
          onboarding: res.data.onboarding,
          efcs: res.data.efcs,
          preapprovals: res.data.preapprovals,
        });

        const onboarding = res.data.onboarding;
        checkForNeededDashboardInfo(onboarding);
        const profileProgress = calculateProfilePercent({
          onboarding,
          preapprovals: res.data.preapprovals,
        });
        const res2 = await updateProfileProgress(
          res.data.case_id,
          profileProgress,
        );

        // Resume where the user last left off instead of restarting at /home
        const lastPageRoute = await getLastPageRoute(res.data.case_id);
        router.push(lastPageRoute);
      }
    } catch (error) {
      if (
        get(error?.response, "data.result.message") ===
        "Credentials are invalid."
      ) {
        setError("email", {
          type: "manual",
          message: "Incorrect email or password",
        });
      } else {
        handleApiError(error);
      }
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  const handleSignUpClick = () => {
    // const utmParams = {
    //   utm_campaign: "15042440-Google Post Launch MyCAP 2.0 Awareness",
    //   utm_source: "ppc",
    //   utm_medium: "ppc",
    //   utm_content: "google awareness mycap free",
    // };

    // const queryString = Object.entries(utmParams)
    //   .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    //   .join("&");

    router.push(`/create_email`);
  };

  useEffect(
    () => {
      clearErrors();
    },
    watch(["email", "password"]),
  );

  return (
    <div className="min-h-screen grid grid-cols-2 bg-[#1A85C1] h-full">
      <Head>
        <title>Sign In - College Aid Pro™</title>
      </Head>

      {/* Left Section - Sign In Form */}
      <div className="col-span-2 md:col-span-1 p-8 flex flex-col items-center justify-center relative bg-white m-3 rounded-3xl">
        {/* Logo */}
        <div className="md:mb-8 absolute top-0 flex flex-wrap justify-center lg:justify-between w-full items-center px-10 py-5">
          <Image
            alt="College Aid Pro"
            priority={true}
            src="/images/cap_main_logo_blue.png"
            width={255}
            height={170}
          />
          <div className="text-right text-sm text-gray-600">
            Don&apos;t have an account?{" "}
            <button
              onClick={handleSignUpClick}
              className="text-blue-600 hover:text-blue-600 underline bg-transparent border-none cursor-pointer no-underline"
              disabled={isSubmitting}
            >
              Sign Up
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="w-full max-w-md mt-14 md:mt-0">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold md:mb-5 mb-1">Sign in</h1>
            <p className="text-gray-600">
              Welcome back! Please enter your details.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <InputLabel htmlFor="email">Email</InputLabel>
              <TextField
                id="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                placeholder="Enter your email"
                autoComplete="email"
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Box>

            <Box>
              <InputLabel htmlFor="password">Password</InputLabel>
              <TextField
                id="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
                type="password"
                autoComplete="current-password"
                size="small"
                variant="outlined"
                fullWidth
                className="bg-white"
                error={!!errors.password}
                helperText={errors.password?.message}
              />
            </Box>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <FormControlLabel
                control={<Checkbox />}
                label="Remember for 30 days"
                className="text-gray-700"
              />
              <Link
                href="/forgot-password"
                className="text-[#1A85C1] hover:text-blue-600 text-sm"
                disabled={isSubmitting}
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="w-full bg-primary-400 text-white py-3 rounded-xl hover:bg-blue-600 transition-colors"
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Sign In"
              )}
            </button>
          </form>
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-gray-300"></div>
            <div className="mx-4 text-gray-500">OR</div>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>
          <GoogleLoginButton className="my-4" />
        </div>

        <div className="absolute md:bottom-10 bottom-5 right-8 text-sm text-gray-500 flex gap-5">
          <Link href="/Privacy Policy.pdf" className="text-gray-700">
            Privacy & Terms
          </Link>
          <Link href="mailto:info@collegeaidpro.com" className="text-gray-700">
            Contact Us
          </Link>
        </div>
      </div>

      {/* Right Section - Testimonials & Preview */}
      <div className="hidden md:flex md:flex-col gap-5 md:col-span-1 text-white py-8 px-4 pr-3 pb-4 relative h-full md:max-w-4xl mx-auto">
        {/* Testimonial Slider */}
        <div className="relative px-4 pr-7">
          <div className="transition-all duration-500 ease-in-out">
            <div className="mb-6 lg:text-xl text-lg font-medium leading-relaxed lg:h-[140px] h-[200px] flex items-center justify-center">
              <Link
                href={testimonials[currentSlide].href}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer text-white"
              >
                {" "}
                {testimonials[currentSlide].text}
              </Link>
            </div>

            <div className="flex flex-wrap lg:justify-between justify-center">
              {/* Dots */}
              <div className="flex items-center gap-4">
                <Avatar>{testimonials[currentSlide].avatar}</Avatar>
                <div>
                  <div className="font-semibold">
                    {testimonials[currentSlide].name}
                  </div>
                  <div className="flex gap-1">
                    {[...Array(testimonials[currentSlide].rating)].map(
                      (_, i) => (
                        <span key={i} className="text-yellow-400">
                          ★
                        </span>
                      ),
                    )}
                  </div>
                </div>
              </div>
              {/* Buttons */}
              <div className="relative md:mr-14 m-auto items-center">
                <button
                  onClick={prevSlide}
                  aria-label="Previous testimonial"
                  className="absolute right-44 top-[2.2rem] -translate-y-1/2 -translate-x-full"
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <button
                  onClick={nextSlide}
                  aria-label="Next testimonial"
                  className="absolute left-44 top-[2.2rem] -translate-y-1/2 translate-x-full"
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>

                {/* Navigation Dots */}
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Go to testimonial ${index + 1} of ${testimonials.length}`}
                      aria-current={currentSlide === index ? "true" : undefined}
                      className={`w-2 h-2 rounded-full transition-all ${
                        currentSlide === index
                          ? "bg-white scale-125"
                          : "bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Preview */}
        {/* <div
          className="w-full h-full flex-1 rounded-lg shadow-lg"
          style={{
            backgroundImage: "url('/browser-login.png')",
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div> */}

        <div className="w-full h-full relative flex-1 overflow-hidden">
          <Image
            src={testimonials[currentSlide].banner}
            alt="Dashboard Preview"
            layout="fill"
            objectFit="contain"
            priority
            className="transition-all duration-500 ease-in-out"
          />
        </div>
      </div>
    </div>
  );
}

export default LoginNewVer;
